// === CommentsStep.tsx ===
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CommentsForm } from "./comments-form"
import { CommentsFormData, commentsSchema } from "@pma/utils/work-diaries-schema"
import { DictType } from "@pma/types"
import axios from "axios"
import { SERVER_URL, WORK_DIARY_GET_NOTE_BY_ID } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useQuery } from "@tanstack/react-query"
import { Button } from "../../button"
import useTranslator from "@pma/hooks/use-translator"
import { useMemo } from "react"

interface CommentsStepProps {
  workDiaries: DictType | undefined
  project?: DictType
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
}

export function CommentsStep({ workDiaries, setActiveStep }: CommentsStepProps) {
  const { translate } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const form = useForm<CommentsFormData>({
    resolver: zodResolver(commentsSchema),
    defaultValues: {
      contractorApproval: workDiaries?.contractor_approval,
      supervisorApproval: workDiaries?.supervisor_approval,
    },
  })

  const getCommentList = async () => {
    const response = await axios.get(
      SERVER_URL + WORK_DIARY_GET_NOTE_BY_ID + `/${workDiaries?.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const {
    data: commentList,
    refetch: refetchComment,
    isLoading,
  } = useQuery({
    queryKey: ["diary-comment", workDiaries?.id],
    queryFn: () => getCommentList(),
  })

  const sortedCommentsList = useMemo(
    () => commentList?.sort((a: DictType, b: DictType) => b.created_at - a.created_at) || [],
    [commentList],
  )

  return (
    <FormProvider {...form}>
      <CommentsForm
        workDiaries={workDiaries}
        comments={sortedCommentsList}
        isLoading={isLoading}
        refetchComment={refetchComment}
      />
      <div className='flex flex-col justify-between gap-4 border-t pt-6 sm:flex-row'>
        <Button
          type='button'
          variant='outline'
          className='w-full sm:w-auto'
          onClick={() => setActiveStep(2)}
        >
          {translate("Previous")}
        </Button>
        <Button type='submit' className='w-full sm:w-auto' onClick={() => setActiveStep(4)}>
          {translate("Preview")}
        </Button>
      </div>
    </FormProvider>
  )
}
